import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Lock, ArrowRight } from 'lucide-react';
import myListIQLogo from '@/assets/mylistiq-logo-new.png';
import axiosInstance from '@/config/axiosInstance';
import { endpoints } from '@/config/apiEndpoints';

const NewPassword = () => {
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { uidb64, token } = useParams();

  const handleResetPassword = async () => {
    setError('');
    setSuccess('');
    if (!passwordData.password || !passwordData.confirmPassword) {
      setError('Both fields are required.');
      return;
    }
    if (passwordData.password !== passwordData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (passwordData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    try {
      const res = await axiosInstance.post(
        `${endpoints.resetPassword}${uidb64}/${token}/`,
        {
          new_password: passwordData.password,
          confirm_password: passwordData.confirmPassword
        }
      );
      if (res.data.message) {
        setSuccess('Password has been reset successfully.');
        setTimeout(() => navigate('/signup'), 2000);
      } else {
        setError('Something went wrong.');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Failed to reset password.'
      );
    }
  };

  const isFormValid =
    passwordData.password &&
    passwordData.confirmPassword &&
    passwordData.password === passwordData.confirmPassword &&
    passwordData.password.length >= 6;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-6">
            <img src={myListIQLogo} alt="MyListIQ" className="h-16 w-auto mr-4" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-2">Set New Password</h2>
          <p className="text-muted-foreground">Enter your new password below</p>
        </div>

        <Card className="shadow-elevated bg-gradient-card border-0">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    className="pl-10"
                    value={passwordData.password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        password: e.target.value
                      })
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    className="pl-10"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value
                      })
                    }
                  />
                </div>
                {passwordData.confirmPassword &&
                  passwordData.password !== passwordData.confirmPassword && (
                    <p className="text-xs text-destructive mt-1">
                      Passwords do not match
                    </p>
                  )}
              </div>

              {error && <div className="text-red-500 text-sm">{error}</div>}
              {success && <div className="text-green-600 text-sm">{success}</div>}

              <Button
                variant="cta"
                size="lg"
                className="w-full"
                onClick={handleResetPassword}
                disabled={!isFormValid}
              >
                Reset Password
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="text-center">
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/signup" className="text-muted-foreground hover:text-primary">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Login
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewPassword;